'use client';

/**
 * Site-wide footer — Industry design system (v3), columned layout.
 *
 * Structure follows the standards-body convention (cf. Gold Standard):
 * an identity block on the left, three labelled link columns (INFORM /
 * EXPLORE / ENGAGE), and a hairline-separated legal bar at the bottom.
 * Every route the previous footer carried keeps a link — including
 * /verify, which must be reachable from every public page.
 * No street address is shown: the contact route and e-mail are the
 * programme's published points of contact.
 */
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Contours } from './ds/Contours';

const HIDE_ON_PREFIXES = ['/portal', '/vvb', '/reviewer', '/auth', '/admin'];

type FooterLink = { href: string; label: string };

const COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: 'Inform',
    links: [
      { href: '/news', label: 'News' },
      { href: '/consultations', label: 'Consultations' },
      { href: '/transparency', label: 'Transparency' },
      { href: '/how-it-works', label: 'How it works' },
      { href: '/ai', label: 'AI & human oversight' },
      { href: '/reviewer-guide', label: 'Reviewer guide' },
    ],
  },
  {
    heading: 'Explore',
    links: [
      { href: '/registry', label: 'Registry' },
      { href: '/projects', label: 'Projects' },
      { href: '/methodologies', label: 'Standards & methodologies' },
      { href: '/transparency/documents', label: 'Standard documents' },
      { href: '/verify', label: 'Verify a credit' },
      { href: '/retirements', label: 'Retirements' },
      { href: '/marketplace', label: 'Marketplace' },
    ],
  },
  {
    heading: 'Engage',
    links: [
      { href: '/submit', label: 'Submit a project' },
      { href: '/contact', label: 'Contact' },
      { href: '/governance', label: 'Governance' },
      { href: '/grievances', label: 'Grievances & appeals' },
      { href: '/about', label: 'About' },
    ],
  },
];

const LEGAL_LINKS: FooterLink[] = [
  { href: '/terms', label: 'Terms' },
  { href: '/privacy', label: 'Privacy' },
  { href: '/transparency/documents/claims-policy', label: 'Claims Policy' },
  { href: '/transparency/system-status', label: 'System status' },
];

const linkStyle: React.CSSProperties = {
  color: 'inherit',
  textDecoration: 'none',
};

export function SiteFooter() {
  const pathname = usePathname() || '/';
  if (HIDE_ON_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  return (
    <footer className="site-footer ind-band-deep">
      <Contours layout="corner" opacity={0.55} soundings={false} />
      <div className="ind-container" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '40px 64px',
            justifyContent: 'space-between',
            padding: '64px 0 56px',
          }}
        >
          <div style={{ maxWidth: 340 }}>
            <Link href="/" style={{ ...linkStyle, display: 'block' }}>
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 26,
                  lineHeight: '28px',
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  color: 'var(--color-on-deep)',
                  display: 'block',
                }}
              >
                Marine Conservation
                <br />
                Credits
              </span>
            </Link>
            <span
              style={{
                display: 'block',
                marginTop: 10,
                fontSize: 12,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 600,
                color: 'var(--color-on-deep-faint)',
              }}
            >
              The standards body for the ocean
            </span>
            <p style={{ margin: '24px 0 0', fontSize: 14, lineHeight: '22px', color: 'var(--color-on-deep-muted)' }}>
              Standards, registry and independent verification for marine conservation outcomes. Contribution-first:
              no offsetting, neutrality or net-zero claims against an MCC retirement.
            </p>
            <a
              href="mailto:info@marineconservationcredits.com"
              style={{
                ...linkStyle,
                display: 'inline-block',
                marginTop: 20,
                fontSize: 14,
                lineHeight: '22px',
                color: 'var(--color-on-deep)',
                borderBottom: '1px solid var(--color-on-deep-divider)',
              }}
            >
              info@marineconservationcredits.com
            </a>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '40px 72px',
            }}
          >
            {COLUMNS.map((col) => (
              <div key={col.heading} style={{ minWidth: 160 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--color-on-deep-faint)',
                    marginBottom: 16,
                  }}
                >
                  {col.heading}
                </span>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {col.links.map((l) => (
                    <li key={l.href} style={{ marginBottom: 10 }}>
                      <Link
                        key={l.href}
                        href={l.href}
                        className="site-footer__link"
                        style={{
                          ...linkStyle,
                          fontSize: 14,
                          lineHeight: '22px',
                          color: 'var(--color-on-deep)',
                        }}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px 28px',
            alignItems: 'center',
            padding: '18px 0 36px',
            borderTop: '1px solid var(--color-on-deep-divider)',
            fontSize: 13,
            lineHeight: '24px',
            color: 'var(--color-on-deep-faint)',
          }}
        >
          <span>© 2026 Marine Conservation Credits Programme</span>
          {LEGAL_LINKS.map((l) => (
            <Link key={l.href} href={l.href} className="site-footer__link" style={linkStyle}>
              {l.label}
            </Link>
          ))}
          <span style={{ marginLeft: 'auto', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 12, fontWeight: 600 }}>
            No credits issued yet · Real projects only
          </span>
        </div>
      </div>
    </footer>
  );
}
